<template>
  <cst-page-template class="addenda-page" ds-root :hide-menu="true">
    <header-menu />
    <div class="ds-page ds-page--fill">
      <div class="ds-page__header">
        <h1>Addenda</h1>
        <ds-button data-qa="addenda-add-button" @click="startAdd">
          <svg viewBox="2 2 20 20" fill="currentColor" aria-hidden="true">
            <path
              d="M10.98 19.28V12.98H4.68V10.94H10.98V4.64H13.02V10.94H19.32V12.98H13.02V19.28H10.98Z"
            />
          </svg>
          Nieuw addendum toevoegen
        </ds-button>
      </div>
      <p class="ds-page__intro">
        Je ziet en beheert hier de addenda. Addenda zijn extra pagina's die toegevoegd worden aan de
        CST offerte en order PDF.
      </p>

      <div v-if="loading" class="ds-empty">
        <p class="ds-empty__message">Addenda laden…</p>
      </div>

      <div v-else-if="error" class="ds-empty">
        <p class="ds-empty__message">{{ error }}</p>
        <ds-button variant="ghost" data-qa="addenda-retry-button" @click="load">
          Opnieuw proberen
        </ds-button>
      </div>

      <div v-else-if="!addenda.length" class="ds-empty">
        <h2 class="ds-empty__title">Geen addenda gevonden</h2>
        <p class="ds-empty__message">Maak een nieuw addendum aan om te beginnen.</p>
        <ds-button data-qa="addenda-add-first-button" @click="startAdd">
          Nieuw addendum toevoegen
        </ds-button>
      </div>

      <div v-else class="ds-table">
        <table-filter-chips
          v-if="activeFilterChips.length"
          :chips="activeFilterChips"
          @remove="removeFilter"
          @clear-all="clearFilters"
        />

        <div class="ds-table__scroll">
          <table :style="{ minWidth: tableMinWidth }">
            <sortable-table-header
              :columns="tableColumns"
              :filters="filters"
              :sort-column="sortColumn"
              :sort-direction="sortDirection"
              :has-actions="true"
              :actions-width="actionsColumnWidth"
              @toggle-sort="toggleSort"
              @set-filter="setFilter"
              @clear-filters="clearMultipleFilters"
            />
            <tbody>
              <tr v-for="addendum in pagedAddenda" :key="addendum.Id">
                <td>
                  <span :class="getBrandBadgeClass(addendum.BrandCode)">
                    {{ getBrandName(addendum.BrandCode) }}
                  </span>
                </td>
                <td>
                  <span class="addenda-page__code">{{ formatDealer(addendum.DealerId) }}</span>
                </td>
                <td>
                  <span class="addenda-page__code">{{ addendum.PonCatalogCarPattern || '-' }}</span>
                </td>
                <td>
                  <span class="addenda-page__code">{{ addendum.OptionCode || '-' }}</span>
                </td>
                <td>
                  <span class="ds-chip">{{ formatStock(addendum.Stock) }}</span>
                </td>
                <td>{{ formatDate(addendum.DateFrom) }}</td>
                <td>{{ formatDate(addendum.DateTo) }}</td>
                <td>
                  <div class="ds-table__actions">
                    <button
                      class="ds-icon-button"
                      title="Bekijken"
                      data-qa="addenda-view-button"
                      :disabled="viewingId === addendum.Id"
                      @click="viewAddendum(addendum)"
                    >
                      <ds-spinner v-if="viewingId === addendum.Id" inline />
                      <svg viewBox="2 2 20 20" fill="currentColor" aria-hidden="true">
                        <path
                          d="M12 5.54C16.48 5.54 19.84 9.06 21.76 11.96C19.84 14.86 16.48 18.38 12 18.38C7.52 18.38 4.16 14.86 2.24 11.96C4.16 9.06 7.52 5.54 12 5.54ZM12 7.24C8.58 7.24 6.34 9.62 4.34 11.96C6.34 14.3 8.58 16.68 12 16.68C15.42 16.68 17.66 14.3 19.66 11.96C17.66 9.62 15.42 7.24 12 7.24ZM12 14.48C10.62 14.48 9.48 13.34 9.48 11.96C9.48 10.58 10.62 9.44 12 9.44C13.38 9.44 14.52 10.58 14.52 11.96C14.52 13.34 13.38 14.48 12 14.48Z"
                        />
                      </svg>
                    </button>
                    <button
                      class="ds-icon-button"
                      title="Bewerken"
                      data-qa="addenda-edit-button"
                      @click="startEdit(addendum)"
                    >
                      <svg viewBox="2 2 20 20" fill="currentColor" aria-hidden="true">
                        <path
                          d="M15.82 5.38C16.8 4.4 18.38 4.4 19.38 5.38C20.36 6.36 20.36 7.96 19.38 8.94L8.36 19.96L3.9 20.86L4.8 16.4L15.82 5.38ZM15.72 7.74L6.06 17.4L5.72 19.04L7.36 18.7L17.02 9.04L15.72 7.74Z"
                        />
                      </svg>
                    </button>
                    <button
                      class="ds-icon-button"
                      title="Verwijderen"
                      data-qa="addenda-delete-button"
                      @click="confirmDelete(addendum)"
                    >
                      <svg viewBox="2 2 20 20" fill="currentColor" aria-hidden="true">
                        <path
                          d="M6.42 19.84L6.06 6.46H3.72V4.92H8.3V3.88C8.3 2.76 9.18 1.84 10.48 1.84H13.52C14.82 1.84 15.7 2.76 15.7 3.88V4.92H20.28V6.46H17.94L17.58 19.84H6.42ZM10.06 4.06V4.92H13.94V4.06C13.94 3.66 13.6 3.32 13.2 3.32H10.8C10.4 3.32 10.06 3.66 10.06 4.06ZM7.96 18.3H16.04L16.28 6.46H7.72L7.96 18.3ZM9.58 17.22V7.54H11.08V17.22H9.58ZM12.92 17.22V7.54H14.42V17.22H12.92Z"
                        />
                      </svg>
                    </button>
                  </div>
                </td>
              </tr>
              <tr v-if="!pagedAddenda.length">
                <td colspan="8" class="ds-table__empty">
                  Geen resultaten gevonden voor de huidige filters.
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <ds-pagination
          v-model:page="currentPage"
          v-model:page-size="pageSize"
          :total="filteredAddenda.length"
        />
      </div>
    </div>

    <ds-dialog
      v-if="showForm"
      id="addenda-form"
      :title="formTitle"
      size="md"
      as="form"
      @close="cancelForm"
      @submit.prevent="saveForm"
    >
      <div class="ds-form">
        <div class="ds-form__row">
          <div class="ds-form__field">
            <label for="addendum-brand"
              >Merk
              <span
                class="ds-form__info"
                title="* = geldt voor alle merken&#10;Specifieke code = alleen dat merk (bijv. V, A, S)"
                >ⓘ</span
              ></label
            >
            <select id="addendum-brand" v-model="form.brandCode" class="ds-field">
              <option v-for="option in brandOptions" :key="option.value" :value="option.value">
                {{ option.label }}
              </option>
            </select>
          </div>
          <div class="ds-form__field">
            <label for="addendum-dealer"
              >Dealer ID
              <span
                class="ds-form__info"
                title="0 = geldt voor alle dealers&#10;Specifieke waarde = alleen die dealer"
                >ⓘ</span
              ></label
            >
            <input id="addendum-dealer" v-model="form.dealerId" type="text" class="ds-field" />
          </div>
        </div>

        <div class="ds-form__row">
          <div class="ds-form__field">
            <label for="addendum-pon-catalog-car"
              >Pon Catalog Car
              <span
                class="ds-form__info"
                title="* = alle auto's&#10;PV5DA5 = alle auto's van die typecode&#10;CT1* = wildcard patroon&#10;PV5DA5/25/EDT = één specifieke Pon Catalog Car"
                >ⓘ</span
              ></label
            >
            <input
              id="addendum-pon-catalog-car"
              v-model="form.ponCatalogCarPattern"
              type="text"
              class="ds-field"
            />
          </div>
          <div class="ds-form__field">
            <label for="addendum-option"
              >Optie code
              <span
                class="ds-form__info"
                title="* = algemeen addendum (geldt voor alle offertes)&#10;GWAIXYZ = alleen gekoppeld aan offertes met deze optie in de configuratie"
                >ⓘ</span
              ></label
            >
            <input id="addendum-option" v-model="form.optionCode" type="text" class="ds-field" />
          </div>
          <div class="ds-form__field">
            <label for="addendum-stock"
              >Stock / Config
              <span class="ds-form__info" title="Bepaalt voor welk type auto het addendum geldt"
                >ⓘ</span
              ></label
            >
            <select id="addendum-stock" v-model.number="form.stock" class="ds-field">
              <option :value="-1">Stock én config</option>
              <option :value="0">Alleen config</option>
              <option :value="1">Alleen stock</option>
            </select>
          </div>
        </div>

        <div class="ds-form__row">
          <div class="ds-form__field">
            <label for="addendum-date-from"
              >Geldig van
              <span
                class="ds-form__info"
                title="Wordt gecontroleerd tegen de leverdatum van de offerte"
                >ⓘ</span
              ></label
            >
            <input id="addendum-date-from" v-model="form.dateFrom" type="date" class="ds-field" />
          </div>
          <div class="ds-form__field">
            <label for="addendum-date-to"
              >Geldig tot
              <span
                class="ds-form__info"
                title="Wordt gecontroleerd tegen de leverdatum van de offerte"
                >ⓘ</span
              ></label
            >
            <input id="addendum-date-to" v-model="form.dateTo" type="date" class="ds-field" />
          </div>
        </div>

        <div class="ds-form__field">
          <label for="addendum-pdf">PDF bestand *</label>
          <div class="ds-file-picker">
            <input
              id="addendum-pdf"
              ref="fileInput"
              type="file"
              accept="application/pdf"
              class="ds-file-picker__native"
              @change="onFileChange"
            />
            <ds-button
              variant="ghost"
              size="sm"
              type="button"
              data-qa="addenda-pdf-choose"
              @click="$refs.fileInput.click()"
            >
              <ds-icon name="upload" />
              PDF kiezen
            </ds-button>
            <span
              class="ds-file-picker__name"
              :class="{ 'ds-file-picker__name--empty': !selectedFile }"
            >
              {{ selectedFile ? selectedFile.name : 'Geen bestand gekozen' }}
            </span>
          </div>
          <span v-if="fileError" class="ds-form__error">{{ fileError }}</span>
          <span v-if="formEditId && !selectedFile" class="ds-form__hint">
            Upload een nieuw PDF-bestand om het huidige bestand te vervangen.
          </span>
        </div>

        <div v-if="formError" class="ds-alert ds-alert--danger">{{ formError }}</div>
      </div>

      <template #footer>
        <ds-button variant="text" type="button" data-qa="addenda-form-cancel" @click="cancelForm">
          Annuleren
        </ds-button>
        <ds-button type="submit" data-qa="addenda-form-submit" :disabled="formSaving">
          <ds-spinner v-if="formSaving" inline />
          <ds-icon v-else-if="formEditId" name="edit" />
          <ds-icon v-else name="save" />
          {{ formSaving ? 'Opslaan…' : formEditId ? 'Wijzig' : 'Opslaan' }}
        </ds-button>
      </template>
    </ds-dialog>

    <ds-confirm-dialog
      id="addenda-delete"
      :show="!!deleteTarget"
      title="Addendum verwijderen"
      body="Weet je zeker dat je dit addendum wilt verwijderen?"
      confirm-label="Verwijderen"
      cancel-label="Annuleren"
      danger
      :confirm-disabled="deleting"
      @confirm="executeDelete"
      @cancel="deleteTarget = null"
    />
  </cst-page-template>
</template>

<script>
import moment from 'moment'
import CstPageTemplate from '@/templates/CstPageTemplate.vue'
import HeaderMenu from '@/components/generic/presentational/molecules/headerMenu/HeaderMenu.vue'
import SortableTableHeader from '@/pages/warningbanner/SortableTableHeader.vue'
import TableFilterChips from '@/pages/warningbanner/TableFilterChips.vue'
import DsButton from '@/components/ds/atoms/dsButton/DsButton.vue'
import DsIcon from '@/components/ds/atoms/dsIcon/DsIcon.vue'
import DsSpinner from '@/components/ds/atoms/dsSpinner/DsSpinner.vue'
import DsDialog from '@/components/ds/molecules/dsDialog/DsDialog.vue'
import DsPagination from '@/components/ds/molecules/dsPagination/DsPagination.vue'
import DsConfirmDialog from '@/components/ds/molecules/dsConfirmDialog/DsConfirmDialog.vue'
import AddendaService from '@/services/addendaService'
import { getBrandBadgeClass } from '@/helpers/BrandHelper'

const DEFAULT_DATE_TO = '2039-12-31'

const BRAND_MAP = {
  '*': 'Alle merken',
  V: 'Volkswagen',
  A: 'Audi',
  S: 'SEAT',
  N: 'Volkswagen Bedrijfswagens',
  C: 'Skoda',
}

const today = () => moment().format('YYYY-MM-DD')

const EMPTY_FORM = () => ({
  brandCode: '*',
  dealerId: '0',
  ponCatalogCarPattern: '*',
  optionCode: '*',
  stock: -1,
  dateFrom: today(),
  dateTo: DEFAULT_DATE_TO,
})

const EMPTY_FILTERS = () => ({
  BrandCode: '',
  DealerId: '',
  PonCatalogCarPattern: '',
  OptionCode: '',
  DateFrom: '',
  DateTo: '',
})

// Default view only shows addenda that are still valid (end date today or later).
const DEFAULT_FILTERS = () => ({ ...EMPTY_FILTERS(), DateTo: today() })

// Three icon buttons per row.
const ACTIONS_COLUMN_WIDTH = 140

const matchesText = (value, filter) =>
  !filter ||
  String(value ?? '')
    .toLowerCase()
    .includes(filter.toLowerCase())

export default {
  name: 'AddendaPage',
  components: {
    CstPageTemplate,
    HeaderMenu,
    SortableTableHeader,
    TableFilterChips,
    DsButton,
    DsIcon,
    DsSpinner,
    DsDialog,
    DsPagination,
    DsConfirmDialog,
  },
  data() {
    return {
      addenda: [],
      loading: false,
      error: null,

      filters: DEFAULT_FILTERS(),
      sortColumn: null,
      sortDirection: 'asc',

      currentPage: 1,
      pageSize: 25,

      showForm: false,
      formEditId: null,
      form: EMPTY_FORM(),
      selectedFile: null,
      fileError: null,
      formSaving: false,
      formError: null,

      deleteTarget: null,
      deleting: false,
      viewingId: null,
    }
  },
  computed: {
    formTitle() {
      return this.formEditId ? 'Addendum bewerken' : 'Nieuw addendum toevoegen'
    },
    brandOptions() {
      return Object.entries(BRAND_MAP).map(([value, label]) => ({
        value,
        label: `${value} - ${label}`,
      }))
    },
    tableColumns() {
      return [
        { key: 'BrandCode', label: 'Merk', width: '200px', filter: 'text', placeholder: 'Zoek...' },
        {
          key: 'DealerId',
          label: 'Dealer ID',
          width: '120px',
          filter: 'text',
          placeholder: 'Zoek...',
        },
        {
          key: 'PonCatalogCarPattern',
          label: 'Pon Catalog Car',
          width: '130px',
          filter: 'text',
          placeholder: 'Zoek...',
        },
        {
          key: 'OptionCode',
          label: 'Optie code',
          width: '110px',
          filter: 'text',
          placeholder: 'Zoek...',
        },
        // Stock is a category, not an ordering, so it is not sortable.
        { key: 'Stock', label: 'Stock/config', width: '130px', sortable: false },
        { key: 'DateFrom', label: 'Geldig van', width: '130px', filter: 'date' },
        { key: 'DateTo', label: 'Geldig tot', width: '130px', filter: 'date' },
      ]
    },
    pagedAddenda() {
      const start = (this.currentPage - 1) * this.pageSize
      return this.sortedAddenda.slice(start, start + this.pageSize)
    },
    actionsColumnWidth() {
      return `${ACTIONS_COLUMN_WIDTH}px`
    },
    // Sum of the declared column widths plus the actions column. Without a
    // definite table width, `table-layout: fixed` lets cell content inflate the
    // columns instead of the other way round.
    tableMinWidth() {
      const columns = this.tableColumns.reduce(
        (sum, col) => sum + (parseFloat(col.width) || 140),
        0
      )
      return `${columns + ACTIONS_COLUMN_WIDTH}px`
    },
    activeFilterChips() {
      const chips = []
      if (this.filters.BrandCode)
        chips.push({ key: 'BrandCode', label: `Merk: "${this.filters.BrandCode}"` })
      if (this.filters.DealerId)
        chips.push({ key: 'DealerId', label: `Dealer ID: "${this.filters.DealerId}"` })
      if (this.filters.PonCatalogCarPattern)
        chips.push({
          key: 'PonCatalogCarPattern',
          label: `Pon Catalog Car: "${this.filters.PonCatalogCarPattern}"`,
        })
      if (this.filters.OptionCode)
        chips.push({ key: 'OptionCode', label: `Optie code: "${this.filters.OptionCode}"` })
      if (this.filters.DateFrom)
        chips.push({ key: 'DateFrom', label: `Geldig van ≥ ${this.filters.DateFrom}` })
      if (this.filters.DateTo)
        chips.push({ key: 'DateTo', label: `Geldig tot ≥ ${this.filters.DateTo}` })
      return chips
    },
    filteredAddenda() {
      return this.addenda.filter(
        (a) =>
          (matchesText(a.BrandCode, this.filters.BrandCode) ||
            matchesText(this.getBrandName(a.BrandCode), this.filters.BrandCode)) &&
          matchesText(a.DealerId, this.filters.DealerId) &&
          matchesText(a.PonCatalogCarPattern, this.filters.PonCatalogCarPattern) &&
          matchesText(a.OptionCode, this.filters.OptionCode) &&
          (!this.filters.DateFrom || (a.DateFrom || '') >= this.filters.DateFrom) &&
          (!this.filters.DateTo || !a.DateTo || a.DateTo >= this.filters.DateTo)
      )
    },
    sortedAddenda() {
      if (!this.sortColumn) return this.filteredAddenda
      const col = this.sortColumn
      const dir = this.sortDirection === 'asc' ? 1 : -1
      return [...this.filteredAddenda].sort(
        (a, b) => String(a[col] ?? '').localeCompare(String(b[col] ?? ''), 'nl') * dir
      )
    },
  },
  watch: {
    filters: {
      deep: true,
      handler() {
        this.currentPage = 1
      },
    },
  },
  created() {
    this.load()
  },
  methods: {
    async load() {
      this.loading = true
      this.error = null
      try {
        const response = await AddendaService.getAllAddenda()
        this.addenda = response.data
      } catch {
        this.error = 'Er is een fout opgetreden bij het ophalen van de addenda.'
      } finally {
        this.loading = false
      }
    },
    startAdd() {
      this.formEditId = null
      this.form = EMPTY_FORM()
      this.selectedFile = null
      this.fileError = null
      this.formError = null
      this.showForm = true
    },
    startEdit(addendum) {
      this.formEditId = addendum.Id
      this.form = {
        brandCode: addendum.BrandCode || '*',
        dealerId: addendum.DealerId || '',
        ponCatalogCarPattern: addendum.PonCatalogCarPattern || '',
        optionCode: addendum.OptionCode || '',
        stock: addendum.Stock ?? -1,
        dateFrom: this.toDateInput(addendum.DateFrom),
        dateTo: this.toDateInput(addendum.DateTo),
      }
      this.selectedFile = null
      this.fileError = null
      this.formError = null
      this.showForm = true
    },
    cancelForm() {
      this.showForm = false
      this.formError = null
      this.fileError = null
    },
    onFileChange(event) {
      const file = event.target.files[0]
      this.fileError = null
      if (!file) {
        this.selectedFile = null
        return
      }
      if (file.type !== 'application/pdf') {
        this.fileError = 'Alleen PDF bestanden zijn toegestaan.'
        this.selectedFile = null
        this.$refs.fileInput.value = ''
        return
      }
      if (file.size > 10_000_000) {
        this.fileError = 'Bestand mag maximaal 10 MB zijn.'
        this.selectedFile = null
        this.$refs.fileInput.value = ''
        return
      }
      this.selectedFile = file
    },
    async saveForm() {
      this.fileError = null
      this.formError = null
      if (!this.formEditId && !this.selectedFile) {
        this.fileError = 'Een PDF bestand is verplicht.'
        return
      }
      this.formSaving = true
      try {
        const fields = {
          brandCode: this.form.brandCode || null,
          dealerId: this.form.dealerId || null,
          ponCatalogCarPattern: this.form.ponCatalogCarPattern || null,
          optionCode: this.form.optionCode || null,
          stock: this.form.stock ?? null,
          dateFrom: this.form.dateFrom ? `${this.form.dateFrom}T00:00:00` : null,
          dateTo: this.form.dateTo ? `${this.form.dateTo}T00:00:00` : null,
        }
        if (this.formEditId) {
          await AddendaService.updateAddendum(this.formEditId, this.selectedFile, fields)
        } else {
          await AddendaService.createAddendum(this.selectedFile, fields)
        }
        this.showForm = false
        await this.load()
      } catch (e) {
        this.formError =
          e?.response?.data?.Message ||
          'Er is een fout opgetreden bij het opslaan van het addendum.'
      } finally {
        this.formSaving = false
      }
    },
    async viewAddendum(addendum) {
      this.viewingId = addendum.Id
      try {
        const response = await AddendaService.getAddendumById(addendum.Id)
        const sasUrl = response.data?.SasUrl
        if (sasUrl) {
          window.open(sasUrl, '_blank', 'noopener,noreferrer')
        } else {
          console.warn('GetAddendumById response:', response.data)
          alert('Geen PDF URL beschikbaar voor dit addendum.')
        }
      } catch (e) {
        console.error('viewAddendum error:', e?.response?.status, e?.response?.data)
        alert(`Fout bij ophalen addendum: ${e?.response?.status ?? e?.message}`)
      } finally {
        this.viewingId = null
      }
    },
    confirmDelete(addendum) {
      this.deleteTarget = addendum
    },
    async executeDelete() {
      this.deleting = true
      try {
        await AddendaService.deleteAddendum(this.deleteTarget.Id)
        this.deleteTarget = null
        await this.load()
      } catch {
        this.deleteTarget = null
      } finally {
        this.deleting = false
      }
    },
    toggleSort(column) {
      if (this.sortColumn === column) {
        this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc'
        if (this.sortDirection === 'asc') this.sortColumn = null
      } else {
        this.sortColumn = column
        this.sortDirection = 'asc'
      }
    },
    setFilter(key, value) {
      this.filters[key] = value
    },
    clearMultipleFilters(keys) {
      keys.forEach((key) => {
        this.filters[key] = ''
      })
    },
    removeFilter(key) {
      this.filters[key] = ''
    },
    clearFilters() {
      this.filters = EMPTY_FILTERS()
    },
    getBrandBadgeClass,
    // Full brand name in the badge, same as the warning banner page.
    getBrandName(brandCode) {
      if (!brandCode) return '-'
      return BRAND_MAP[brandCode] || brandCode
    },
    // '0' is the sentinel for "every dealer" rather than a dealer number.
    formatDealer(value) {
      if (value === '0' || value === 0) return 'Alle dealers'
      return value || '-'
    },
    formatStock(value) {
      if (value === -1) return 'Stock én config'
      if (value === 0) return 'Alleen config'
      if (value === 1) return 'Alleen stock'
      return '-'
    },
    formatDate(value) {
      if (!value) return '-'
      return value.substring(0, 10)
    },
    toDateInput(value) {
      if (!value) return ''
      return value.substring(0, 10)
    },
  },
}
</script>

<style lang="scss" scoped>
// Layout only. The table, badges, chips, buttons, dialogs and file picker all
// come from the design system (styles/ds/). See styles/ds/README.md before
// adding anything here.

// Dealer numbers, catalog car ids and option codes are codes, not prose: tabular
// figures keep them aligned down the column.
.addenda-page__code {
  font-family: $font-family-mono;
  font-size: 12.5px;
  font-variant-numeric: tabular-nums;
  color: var(--ds-color-text);
}
</style>
